import * as React from 'react';
import styled from 'styled-components';
import CircularProgress from '@material-ui/core/CircularProgress';

import ModalDefaultDraggable from '../molecules/ModalDefaultDraggable';
import { getUsers } from '../api/users';
import { ALL_PAGE_SIZE } from '../constants';
import { CLIENT_TYPES_VALUES, USER_TYPES_VALUES } from '../constants/forms';

interface Individual {
  id: number;
  firstName?: string;
  lastName?: string;
  email?: string;
  isActive?: boolean;
  isDeleted?: boolean;
}

interface Props {
  company: { id: number; companyName?: string; name?: string } | null;
  onClose: () => void;
}

// Individuals link to a company through `parent_company`, which holds the
// UsersCompany id — the same id the Companies rows carry.
const CompanyIndividualsModal: React.FC<Props> = ({ company, onClose }) => {
  const [individuals, setIndividuals] = React.useState<Individual[]>([]);
  const [loading, setLoading] = React.useState(false);

  React.useEffect(() => {
    if (!company?.id) return;
    setLoading(true);
    getUsers({
      userRole: USER_TYPES_VALUES.CLIENT,
      clientType: CLIENT_TYPES_VALUES.INDIVIDUAL,
      parentCompany: company.id,
      ordering: 'first_name',
      limit: ALL_PAGE_SIZE,
      offset: 0,
    })
      .then((res: any) => {
        const raw = res?.data;
        setIndividuals(Array.isArray(raw) ? raw : raw?.results || []);
      })
      .catch(() => setIndividuals([]))
      .finally(() => setLoading(false));
  }, [company?.id]);

  const companyName = company?.companyName || company?.name || '';

  return (
    <ModalDefaultDraggable
      onClose={onClose}
      title={companyName ? `Individuals — ${companyName}` : 'Individuals'}
      wide
      maxHeight="720px"
    >
      <Body>
        <Count>
          {loading
            ? ''
            : `${individuals.length} individual${individuals.length === 1 ? '' : 's'}`}
        </Count>

        {loading && (
          <CenterState>
            <CircularProgress size={28} />
          </CenterState>
        )}

        {!loading && individuals.length === 0 && (
          <CenterState>No individuals are connected to this company.</CenterState>
        )}

        {!loading && individuals.length > 0 && (
          <TableCard>
            <HeaderRow>
              <span>Individual Name</span>
              <span>Email</span>
              <span>Status</span>
            </HeaderRow>
            {individuals.map((person, index) => {
              const name = [person.firstName, person.lastName].filter(Boolean).join(' ');
              const active = person.isActive !== false && !person.isDeleted;
              return (
                <Row key={person.id} $zebra={index % 2 === 1}>
                  <Cell data-label="Individual Name">
                    <Name title={name}>{name || '—'}</Name>
                  </Cell>
                  <Cell data-label="Email">
                    {person.email ? (
                      <EmailLink href={`mailto:${person.email}`} title={person.email}>
                        {person.email}
                      </EmailLink>
                    ) : (
                      '—'
                    )}
                  </Cell>
                  <Cell data-label="Status">
                    <StatusChip $active={active}>{active ? 'Active' : 'Inactive'}</StatusChip>
                  </Cell>
                </Row>
              );
            })}
          </TableCard>
        )}
      </Body>
    </ModalDefaultDraggable>
  );
};

export default CompanyIndividualsModal;

const Body = styled.div`
  font-family: 'Segoe UI', -apple-system, BlinkMacSystemFont, 'Helvetica Neue', Arial, Roboto,
    sans-serif, 'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol';
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const Count = styled.div`
  font-size: 0.8125rem;
  color: var(--neutral-500);
  min-height: 1em;
`;

const CenterState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 32px 0;
  color: var(--neutral-500);
  font-size: 0.875rem;
`;

const TableCard = styled.div`
  border: 1px solid var(--border);
  border-radius: 10px;
  background: var(--neutral-0);
  box-shadow: var(--shadow-xs);
  overflow: hidden;
  box-sizing: border-box;
`;

const columns = 'minmax(0, 1.2fr) minmax(0, 1.5fr) 110px';

const HeaderRow = styled.div`
  display: grid;
  grid-template-columns: ${columns};
  gap: 16px;
  padding: 14px 16px;
  background: var(--table-row-header);
  border-bottom: 1px solid var(--border);
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: var(--neutral-500);

  @media (max-width: 600px) {
    display: none;
  }
`;

const Row = styled.div<{ $zebra?: boolean }>`
  display: grid;
  grid-template-columns: ${columns};
  gap: 16px;
  padding: 14px 16px;
  align-items: center;
  background: ${({ $zebra }) => ($zebra ? 'var(--table-row-alt)' : 'var(--table-row-base)')};
  border-bottom: 1px solid var(--border);
  transition: background-color 0.15s ease;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: var(--table-row-hover);
  }

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
    gap: 6px;
  }
`;

const Cell = styled.div`
  min-width: 0;
  font-size: 0.875rem;
  color: var(--mainText);

  @media (max-width: 600px) {
    &::before {
      content: attr(data-label);
      display: block;
      font-size: 0.6875rem;
      font-weight: 700;
      letter-spacing: 0.06em;
      text-transform: uppercase;
      color: var(--neutral-500);
      margin-bottom: 2px;
    }
  }
`;

const Name = styled.div`
  font-weight: 600;
  color: var(--neutral-800);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const EmailLink = styled.a`
  display: block;
  color: var(--primary-600);
  text-decoration: none;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  &:hover {
    color: var(--primary-700);
    text-decoration: underline;
  }
`;

const StatusChip = styled.span<{ $active: boolean }>`
  display: inline-flex;
  align-items: center;
  padding: 3px 10px;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 600;
  line-height: 1.4;
  white-space: nowrap;
  background: ${({ $active }) => ($active ? 'var(--success-50, #e8f6ee)' : 'var(--neutral-100)')};
  color: ${({ $active }) => ($active ? 'var(--success-700, #196a3b)' : 'var(--neutral-700)')};
  border: 1px solid ${({ $active }) => ($active ? 'var(--success-100, #c5e7d2)' : 'var(--border)')};
`;
